/**
 * Continue registration: validate token from email link, sign in with custom token, redirect.
 * Phase 3: Token handler for authority-flow registration.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { signInWithCustomToken } from 'firebase/auth';
import { getFailoverAwareAuth } from '../../config/firebaseWithFailover';
import { getApiUrl } from '../../utils/backendUrl';
import styles from './Login.module.css';

const ContinueRegistration: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'validating' | 'success' | 'error'>('validating');
  const [message, setMessage] = useState<string>('');
  const validationStartedRef = useRef(false);

  useEffect(() => {
    // Prevent double validation (e.g. React Strict Mode) from burning the token on the second request
    if (validationStartedRef.current) return;
    validationStartedRef.current = true;

    // Read token from URL so we don't depend on router state; supports ?token= and initial load
    const params = new URLSearchParams(
      typeof window !== 'undefined' ? window.location.search : ''
    );
    const token = params.get('token') || searchParams.get('token');
    const tokenTrimmed = token?.trim();
    if (!tokenTrimmed) {
      setStatus('error');
      setMessage('Invalid link. No token provided.');
      return;
    }

    let cancelled = false;

    const run = async () => {
      try {
        const apiBase = getApiUrl();
        const url = `${apiBase}/auth/validate-registration-token?token=${encodeURIComponent(tokenTrimmed)}`;
        const res = await fetch(url, { method: 'GET', credentials: 'omit' });

        if (cancelled) return;

        const data = await res.json().catch(() => ({}));

        if (cancelled) return;

        if (!res.ok || !data.success || !data.customToken) {
          setStatus('error');
          setMessage(
            (data.reason as string) ||
              'This link is invalid or has expired. Please register again or contact support.'
          );
          return;
        }

        if (data.promoCode && data.promoTrialDays) {
          try {
            sessionStorage.setItem(
              'tranzit_promo_hint',
              JSON.stringify({ code: data.promoCode, trialDays: data.promoTrialDays })
            );
          } catch {
            /* ignore storage errors */
          }
        }

        const auth = getFailoverAwareAuth();
        await signInWithCustomToken(auth, data.customToken);

        // Complete redirect even if effect was "cancelled" by Strict Mode cleanup; guard ensures single run
        setStatus('success');
        const userType = (data.userType as string) || '';
        const isBroker = userType === 'broker';
        if (isBroker) {
          navigate('/broker/onboarding', { replace: true });
        } else {
          navigate('/carrier/onboarding', { replace: true });
        }
      } catch (err) {
        if (cancelled) return;
        setStatus('error');
        const rawMessage = err instanceof Error ? err.message : String(err);
        const isConfigError = /BACKEND_URL|API_URL|not set/i.test(rawMessage);
        setMessage(
          isConfigError
            ? 'Service is not configured for this link. Please try again later or contact support.'
            : rawMessage || 'Something went wrong. Please try again or contact support.'
        );
      }
    };

    run();
    return () => {
      // Only cancel if this is a duplicate run (guard prevented it from starting). The run that set the ref must complete or we stay stuck.
      if (!validationStartedRef.current) cancelled = true;
    };
  }, [searchParams, navigate]);

  if (status === 'validating') {
    return (
      <div className={styles.loginContainer}>
        <div className={styles.loginBox}>
          <h1>Completing registration...</h1>
          <p style={{ textAlign: 'center', marginTop: '1rem' }}>Please wait.</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className={styles.loginContainer}>
        <div className={styles.loginBox}>
          <h1>Link expired or invalid</h1>
          <p style={{ textAlign: 'center', marginTop: '1rem', color: '#666' }}>{message}</p>
          <button
            type="button"
            className={styles.loginButton}
            onClick={() => navigate('/login', { state: { message } })}
            style={{ marginTop: '1.5rem' }}
          >
            Go to Login
          </button>
          <button
            type="button"
            className={styles.registerButton}
            onClick={() => navigate('/register')}
            style={{ marginTop: '0.5rem' }}
          >
            Register again
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default ContinueRegistration;
